import { ListCompanyArtifactCounts } from '@/gen/inventory/v1/artifact_service_pb'

import { useTrackCallback } from '@/lib/analytics/events'
import { UrlParam, useUrlParams } from '@/lib/url/url-param.hook'

import { useAssessmentAnalytics } from '@/pages/assessment/analytics'
import { ASSESSMENT_TABS } from '@/pages/assessment/assessment-tabs/assessment-tabs'

import { Skeleton } from '@/components/lemmala-design-system/skeleton/skeleton'

import { OverviewCard } from './overview-card'

export type ArtifactsCardProps = {
  artifactCounts?: ListCompanyArtifactCounts
  isLoading?: boolean
  readonly?: boolean
}

export const ArtifactsCard = ({
  artifactCounts,
  isLoading = false,
  readonly = false,
}: ArtifactsCardProps) => {
  const trackingContext = useAssessmentAnalytics()
  const { updateParam } = useUrlParams()
  const trackTabShortcutClick = useTrackCallback(
    'third-party.assessment.overview.tab-shortcut.click',
  )
  const nonArchivedCount = artifactCounts?.nonArchived ?? 0
  const publicArtifactsCount = artifactCounts?.web ?? 0
  const uploadedArtifactsCount = nonArchivedCount - publicArtifactsCount
  const hasUploadedArtifacts = uploadedArtifactsCount > 0
  const label = readonly ? 'View artifacts' : 'Upload artifacts'

  if (isLoading) {
    return (
      <OverviewCard
        title={ASSESSMENT_TABS.ARTIFACTS}
        mainNumber={<Skeleton className='h-9 w-14' />}
        details={[
          <Skeleton key='uploaded' className='h-4 w-32' />,
          <Skeleton key='public' className='h-4 w-24' />,
        ]}
        action={{ label }}
      />
    )
  }

  return (
    <OverviewCard
      title={ASSESSMENT_TABS.ARTIFACTS}
      mainNumber={nonArchivedCount}
      variant={hasUploadedArtifacts ? 'default' : 'disabled'}
      actionVariant={readonly || hasUploadedArtifacts ? 'ghost' : 'default'}
      details={[`${uploadedArtifactsCount} Uploaded`, `${publicArtifactsCount} Public`]}
      action={{
        label,
        onClick: () => {
          if (trackingContext) {
            trackTabShortcutClick({
              ...trackingContext,
              destination: 'artifacts',
              label,
            })
          }

          updateParam(
            readonly ? UrlParam.PAST_ASSESSMENT_TAB : UrlParam.ASSESSMENT_TAB,
            ASSESSMENT_TABS.ARTIFACTS,
          )
        },
      }}
    />
  )
}
